import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { api } from '../../utils/api';
import { subscribeNotifications } from '../../utils/notificationsRealtime';
import { canOpenProfileRoute } from '../../lib/profileAccess';
import { Dropdown } from './TopBarPrimitives';
import { canViewBoard } from '../../modules/mockup-board/access';
import { bellTarget, openMyNotesOnBatch } from '../../modules/mockup-board/page-notes/pnEvents';

interface NotifItem {
  id: string;
  title: string;
  body?: string | null;
  type: string;
  link?: string | null;
  isRead: boolean;
  createdAt: string;
  sourceEventId?: string | null;
}

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (!t || Number.isNaN(t)) return '';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function notifColor(type: string): string {
  const t = (type || '').toLowerCase();
  if (t.includes('won') || t.includes('approv')) return 'var(--forest)';
  if (t.includes('reject') || t.includes('sla') || t.includes('breach')) return 'var(--wine)';
  if (t.includes('discount') || t.includes('hot') || t.includes('assign')) return 'var(--saffron)';
  return 'var(--info)';
}

export function TopBarNotifications({open,onToggle,onClose}:{open:boolean;onToggle:()=>void;onClose:()=>void}) {
  const navigate=useNavigate();
  const {user}=useAuthStore();
  const [notifs,setNotifs]=useState<NotifItem[]>([]);
  const [unreadCount,setUnreadCount]=useState(0);
  // Notifications (BOUNDARY A): initial load + AURA SSE live refresh
  // (a `notification` event → re-load). A slow 5-min fallback poll self-heals
  // if the stream drops or realtime isn't configured on this instance
  // (subscribeNotifications stays quiet then). No Supabase client in the bundle.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [list, cnt] = await Promise.all([
          api.get('/notifications', { params: { limit: 15 } }),
          api.get('/notifications/unread-count'),
        ]);
        if (!alive) return;
        setNotifs((list.data?.data as NotifItem[]) ?? []);
        setUnreadCount(cnt.data?.data?.count ?? 0);
      } catch {
        /* offline — keep last state */
      }
    };
    load();
    // Live: re-load the bell whenever a notification event arrives for me.
    // EventSource (cookie-auth, same-origin) — auto-reconnects on drop.
    const unsub = subscribeNotifications(() => {
      if (alive) load();
    });
    // Fallback slow poll (also the only refresh on non-realtime boxes).
    const t = setInterval(load, 300000);
    return () => {
      alive = false;
      clearInterval(t);
      unsub();
    };
  }, []);

  const markNotifRead = (n: NotifItem) => {
    if (!n.isRead) {
      setNotifs((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
      api.patch(`/notifications/${n.id}/read`).catch(() => {});
    }
    // 2026-10-12 page notes: a writer who cannot open the Mockup Board gets My notes on that batch (the
    // page-notes toolbar listens); with no toolbar on the page, the link (the note's own page) is used.
    if (n.type === 'page_note_update' && !canViewBoard(user?.role?.name)) {
      const t = bellTarget(n);
      if (t && openMyNotesOnBatch(t)) { onClose(); return; }
    }
    if (n.link && canOpenProfileRoute(user, n.link.split('?')[0], n.link.includes('?') ? '?' + n.link.split('?')[1] : '')) {
      onClose();
      navigate(n.link);
    }
  };

  const markAllNotifsRead = () => {
    setNotifs((prev) => prev.map((x) => ({ ...x, isRead: true })));
    setUnreadCount(0);
    api.patch('/notifications/read-all').catch(() => {});
  };
  return (
      <div className="relative">
        <button
          type="button"
          onClick={onToggle}
          aria-label="Notifications"
          className="relative flex items-center justify-center rounded-md max-sm:min-h-[40px] max-sm:min-w-[40px]"
          style={{ width: 30, height: 30, color: 'var(--ink-2)', background: 'transparent' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <Bell size={15} />
          {unreadCount > 0 && (
            <span
              className="absolute flex items-center justify-center"
              style={{
                top: 1, right: -1, minWidth: 14, height: 14, padding: '0 3px',
                borderRadius: 7, background: 'var(--saffron)', color: '#fff',
                fontSize: 9, fontWeight: 700, lineHeight: 1,
              }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
        {open && (
          <Dropdown width={340} align="right" padding={0}>
            <div
              className="flex items-center justify-between border-b px-3.5 py-2.5"
              style={{ borderColor: 'var(--line)' }}
            >
              <span className="text-[13px] font-medium" style={{ color: 'var(--ink-0)' }}>
                Notifications
              </span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllNotifsRead}
                  className="text-[11px] font-medium"
                  style={{ color: 'var(--saffron)', background: 'transparent' }}
                >
                  Mark all read
                </button>
              )}
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {notifs.length === 0 ? (
                <div
                  className="px-3.5 py-6 text-center text-[12px]"
                  style={{ color: 'var(--ink-4)' }}
                >
                  You&apos;re all caught up.
                </div>
              ) : (
                notifs.map((n) => (
                  <NotificationRow
                    key={n.id}
                    title={n.title}
                    body={n.body ?? ''}
                    ago={timeAgo(n.createdAt)}
                    color={notifColor(n.type)}
                    unread={!n.isRead}
                    onClick={() => markNotifRead(n)}
                  />
                ))
              )}
            </div>
          </Dropdown>
        )}
      </div>

  );
}

function NotificationRow({
  title, body, ago, color, unread, onClick,
}: { title: string; body: string; ago: string; color: string; unread?: boolean; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      className="flex gap-2.5 border-b px-3.5 py-2.5"
      style={{
        borderColor: 'var(--ivory-3)',
        cursor: onClick ? 'pointer' : 'default',
        background: unread ? 'var(--ivory-2)' : 'transparent',
      }}
    >
      <span
        className="mt-1.5 rounded-full"
        style={{ width: 6, height: 6, background: unread ? color : 'var(--line)', flexShrink: 0 }}
      />
      <div className="flex-1">
        <div
          className="text-[13px]"
          style={{ color: 'var(--ink-0)', fontWeight: unread ? 700 : 500 }}
        >
          {title}
        </div>
        <div className="whitespace-pre-line text-[12px]" style={{ color: 'var(--ink-3)' }}>{body}</div>
      </div>
      {ago && (
        <div className="text-[11px]" style={{ color: 'var(--ink-4)' }}>{ago}</div>
      )}
    </div>
  );
}

